import * as z from "zod";
import {
  ArtifactRefSchema,
  CommonMetadataSchema,
  FilePrerequisiteSchema,
  type ManifestValidationResult,
  UserInputSchema,
  validateManifest,
} from "./_shared.js";

const SkillMetadataSchema = CommonMetadataSchema.extend({
  parameters: z.record(z.string(), z.unknown()).optional(),
  artifacts: z
    .object({
      requires: z.array(ArtifactRefSchema).optional(),
      produces: z.array(ArtifactRefSchema).optional(),
      consumes: z.array(ArtifactRefSchema).optional(),
    })
    .optional(),
  prerequisites: z
    .object({
      files: z.array(FilePrerequisiteSchema).optional(),
      inputs_required: z.array(UserInputSchema).optional(),
      inputs_optional: z.array(UserInputSchema).optional(),
      // Parsed by the resolver's parseSkillRequirements; Zod would strip them otherwise.
      reads: z.array(z.object({ path: z.string(), description: z.string().optional() })).optional(),
      produces: z
        .array(z.object({ path: z.string(), description: z.string().optional() }))
        .optional(),
      resources: z
        .array(
          z.object({
            id: z.string(),
            operations: z.array(z.string()).optional(),
            optional: z.boolean().optional(),
            description: z.string().optional(),
          }),
        )
        .optional(),
      connectors: z
        .array(
          z.object({
            driver: z.string(),
            access: z.enum(["read-only", "read-write"]).optional(),
          }),
        )
        .optional(),
    })
    .optional(),
  produces: z.array(z.object({ path: z.string(), description: z.string().optional() })).optional(),
});

export const SkillManifestSchema = z.looseObject({
  name: z.string().min(1),
  description: z.string().min(1),
  version: z.string().optional(),
  license: z.string().optional(),
  compatibility: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  // agentskills.io standard field. Kept as `unknown` (not `string`) so a legacy
  // array form never hard-fails validateSkill — the string-typing rule is
  // advisory, owned by checkAgentSkillConformance (warn, not error).
  "allowed-tools": z.unknown().optional(),

  // Legacy fields (SKILL.md convention)
  source: z.string().optional(),
  user_inputs: z.array(UserInputSchema).optional(),
  reads_from: z.array(z.string()).optional(),
  writes_to: z.array(z.string()).optional(),

  metadata: SkillMetadataSchema.optional(),
});

export type SkillManifest = z.infer<typeof SkillManifestSchema>;

export function validateSkill(data: unknown): ManifestValidationResult<SkillManifest> {
  return validateManifest(SkillManifestSchema, data);
}
